import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDocument, getCollection, api, updateDocument, logout } from "../config";
import moment from "moment";
import { canLoadPrivateOnboardingData } from "../../services/onboardingAccess";

// Async thunks for data loading
export const loadOnboardingData = createAsyncThunk(
  "onboarding/loadOnboardingData",
  async ({ inviteId, authenticatedUserId }, { dispatch, rejectWithValue }) => {
    try {
      let doc = await getDocument('onboarding_invites', inviteId);
      // Referral / preview accounts usually have no invite yet (they only get one
      // from a preferred-day booking or an admin-sent onboarding link), so the
      // upgrade CTA can land here without one. Provision it on demand — the
      // callable also creates the user_subscriptions doc the onboard step needs.
      const canLoadPrivateData = canLoadPrivateOnboardingData(
        inviteId,
        authenticatedUserId
      );
      if (!doc && canLoadPrivateData) {
        await api.onboardingProvision({});
        doc = await getDocument('onboarding_invites', inviteId);
      }
      if (!doc) throw new Error('Onboarding invite not found');

      if(doc.passwordSet) dispatch(setPasswordSet(true));

      // Load user data to get email and name
      const userData = await getDocument('users', inviteId);
      if (!userData) throw new Error('User data not found');

      const locations = await getCollection('locations', {
        where: [
          [`users.${inviteId || ''}`, "==", true]
        ],
      });

      if (locations.length === 0) throw new Error('Locations not found');

      // A completed invite only means "done" while the subscription is still
      // live. If it was cancelled (stripeSubscription cleared) the user is here
      // to reactivate, so don't drop them on the success screen (see the
      // fulfilled reducer's currentStep=5 jump).
      let hasStripeSubscription = false;
      if (canLoadPrivateData) {
        const subscription = await getDocument('user_subscriptions', inviteId);
        hasStripeSubscription = !!subscription?.stripeSubscription?.id;
      }

      return {
        onboarding: doc,
        locations,
        user: userData,
        hasStripeSubscription,
        subscriptionStatusLoaded: canLoadPrivateData,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const completeOnboarding = createAsyncThunk(
  "onboarding/completeOnboarding",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { onboarding, account } = getState();
      const { selectedLocations, event, stripeToken } = onboarding;
      
      await api.accountOnboard({
        locations: selectedLocations.map(l=>l.id),
        event: event,
        stripeToken: stripeToken
      })
      
      await updateDocument({entity: 'onboarding_invites', id: account.user.uid,merge: true}, { completed: true });
      
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add password update async thunk
export const updatePasswordSet = createAsyncThunk(
  "onboarding/updatePasswordSet",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { account } = getState();
      await updateDocument({ entity: "onboarding_invites", id: account.user.uid, merge: true }, { passwordSet: true });
      return;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new async thunk to handle requires recent login
export const handleRequiresRecentLogin = createAsyncThunk(
  "onboarding/handleRequiresRecentLogin",
  async (_, { dispatch }) => {
    try {
      // Sign out the user
      await logout();
      
      // Reset onboarding state to step 0
      dispatch(resetOnboarding());
      dispatch(setCurrentStep(0));
      
      
      return { success: true };
    } catch (error) {
      throw error;
    }
  }
);

export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState: {
    // Loading states
    loading: true,
    processing: false,
    error: null,
    loadingError: null,
    passwordSet: false,

    // Onboarding data
    onboarding: null,
    user: null, // Add user data
    selectedLocations: [],
    
    // Step management
    currentStep: 0, // Start with authentication step
    totalSteps: 5, // 5 steps: 0=auth, 1=summary, 2=locations, 3=fundraiser, 4=payment, 5=success

    event:{
        payout: 20,
        payoutTimeframe: 30,
        cutoffDays: 30,
        allDay: true,
        starts: null,
        ends: null,
        repeatDays: {'Monday': true,'Tuesday': true,'Wednesday': true,'Thursday': true,'Friday': true}
    },

    stripeToken: null,
    
    // Step 0: Authentication
    step0: {
      completed: false,
      validated: false
    },
    
    // Step 2: Location selection
    step2: {
      completed: false,
      validated: false,
    },
    
    // Step 3: Fundraiser details
    step3: {
      completed: false,
      validated: false
    },
    
    // Step 4: Subscription & Payment
    step4: {
      completed: false,
      validated: false
    },
    
    // Form validation errors
    validationErrors: {},
  },
  reducers: {
    // Step navigation
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps) {
        state.currentStep += 1;
      }
    },
    previousStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },
    setPasswordSet: (state, action) => {
      state.passwordSet = action.payload;
      if (action.payload) {
        state.step0.completed = true;
        state.step0.validated = true;
      }
    },
    completeStep0: (state) => {
      state.step0.completed = true;
      state.step0.validated = true;
    },
    
    // Step 2: Location management
    setSelectedLocations: (state, action) => {
      state.selectedLocations = action.payload;
      state.step2.validated = action.payload.length > 0;
    },
    removeLocation: (state, action) => {
      state.selectedLocations = state.selectedLocations.filter(
        location => location.id !== action.payload.id
      );
      state.step2.validated = state.selectedLocations.length > 0;
    },
    completeStep2: (state) => {
      state.step2.completed = true;
      state.step2.validated = true;
    },
    
    // Step 3: Fundraiser details
    setEventField: (state, action) => {
      const { field, value } = action.payload;
      state.event[field] = value;
    },
    toggleEventRepeatDay: (state, action) => {
      const day = action.payload;
      const days = { ...state.step3.eventRepeatDays };
      if (days[day]) {
        delete days[day];
      } else {
        days[day] = true;
      }
      state.step3.eventRepeatDays = days;
    },
    completeStep3: (state) => {
      state.step3.completed = true;
      state.step3.validated = true;
    },
    
    // Step 4: Payment
    setPaymentMethod: (state, action) => {
      state.step4.paymentMethod = action.payload;
    },
    setSubscriptionData: (state, action) => {
      state.step4.subscriptionData = action.payload;
    },
    completeStep4: (state) => {
      state.step4.completed = true;
      state.step4.validated = true;
    },
    
    // Validation
    setValidationErrors: (state, action) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    
    // Reset
    resetOnboarding: (state) => {
      state.currentStep = 0;
      state.step0.completed = false;
      state.step0.validated = false;
      state.step2.completed = false;
      state.step2.validated = false;
      state.step3.completed = false;
      state.step3.validated = false;
      state.step4.completed = false;
      state.step4.validated = false;
      state.validationErrors = {};
      state.error = null;
      state.loadingError = null;
    },
    setStripeToken: (state, action) => {
      state.stripeToken = action.payload;
    },
    // Processing states
    setProcessing: (state, action) => {
      state.processing = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: {
    [loadOnboardingData.pending]: (state) => {
      state.loading = true;
      state.loadingError = null;
    },
    [loadOnboardingData.fulfilled]: (state, action) => {
      state.loading = false;
      // Only land on the success screen if onboarding completed AND the
      // subscription is still live. A cancelled account (stripeSubscription
      // cleared) returns here to reactivate, so let it run the flow instead.
      if(action.payload.onboarding.completed && action.payload.subscriptionStatusLoaded) {
        state.currentStep = action.payload.hasStripeSubscription ? 5 : 0;
      }
      if(action.payload.onboarding.event) {
        state.event = action.payload.onboarding.event;
        if(state.event.starts) state.event.starts = moment(state.event.starts).toDate();
        if(state.event.ends) state.event.ends = moment(state.event.ends).toDate();
      }
      state.onboarding = action.payload.onboarding;
      state.user = action.payload.user; // Store user data
      state.selectedLocations = action.payload.locations.filter(l=>!l.deleted);
      state.step2.validated = action.payload.locations.length > 0; // Updated to step2
    },
    [loadOnboardingData.rejected]: (state, action) => {
      state.loading = false;
      state.loadingError = action.payload;
    },
    [completeOnboarding.pending]: (state) => {
      state.processing = true;
      state.error = null;
    },
    [completeOnboarding.fulfilled]: (state) => {
      state.processing = false;
      // Mark all steps as completed
      state.step0.completed = true;
      state.step2.completed = true;
      state.step3.completed = true;
      state.step4.completed = true;
    },
    [completeOnboarding.rejected]: (state, action) => {
      state.processing = false;
      state.error = action.payload;
    },
  },
});

export const {
  // Step navigation
  setCurrentStep,
  nextStep,
  previousStep,
  
  // Step 0: Authentication
  setPasswordSet,
  completeStep0,
  
  // Step 2: Location management
  setSelectedLocations,
  removeLocation,
  completeStep2,
  
  // Step 3: Fundraiser details
  setEventField,
  toggleEventRepeatDay,
  completeStep3,
  
  // Step 4: Payment
  setPaymentMethod,
  setSubscriptionData,
  completeStep4,
  
  // Validation
  setValidationErrors,
  clearValidationErrors,
  
  // Reset
  resetOnboarding,
  
  // Processing states
  setProcessing,
  setError,
  setStripeToken,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
